import { useEffect, useRef, useState } from "react";
import { api, API } from "@/lib/api";
import { Bot, Send, Trash2, Loader2, Sparkles, User } from "lucide-react";
import { toast } from "sonner";

const SUGGESTIONS = [
  "Conviene accettare un matrimonio da 1.500€ con 2 tecnici?",
  "Come posso trovare nuovi clienti oltre ai matrimoni?",
  "Quali sono i miei eventi più redditizi quest'anno?",
  "Dovrei assumere un dipendente fisso o restare con i freelance?",
  "Come posso ridurre i costi fissi del magazzino?",
];

export default function AIPartner() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const scrollRef = useRef(null);

  useEffect(() => {
    api.chatHistory().then((d) => { setMessages(d); setLoadingHistory(false); }).catch(() => setLoadingHistory(false));
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, streaming]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: msg, session_id: "betti-main" }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        acc += decoder.decode(value, { stream: true });
        setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", content: acc }; return c; });
      }
    } catch {
      toast.error("Errore di connessione con il socio AI");
      setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", content: "Si è verificato un errore. Riprova." }; return c; });
    }
    setStreaming(false);
  };

  const clear = async () => { await api.clearChat(); setMessages([]); toast.success("Conversazione cancellata"); };

  return (
    <div className="flex flex-col h-[calc(100vh-7rem)] lg:h-[calc(100vh-4rem)]">
      <div className="flex items-center justify-between gap-4 pb-4 border-b border-[#222D42]">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center shadow-lg shadow-purple-900/30">
            <Bot className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="font-head text-xl md:text-2xl font-bold tracking-tight text-slate-100">Socio Virtuale</h1>
            <p className="text-slate-500 text-xs">Consigli strategici basati sui tuoi dati reali</p>
          </div>
        </div>
        {messages.length > 0 && (
          <button data-testid="clear-chat-button" onClick={clear} className="text-slate-400 hover:text-red-400 p-2 rounded-lg hover:bg-red-500/10 transition-colors"><Trash2 className="w-4 h-4" /></button>
        )}
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto py-6 space-y-5">
        {loadingHistory ? (
          <div className="flex items-center justify-center h-full text-slate-500"><Loader2 className="w-6 h-6 animate-spin" /></div>
        ) : messages.length === 0 ? (
          <div className="max-w-2xl mx-auto text-center px-4">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center mx-auto mb-5 shadow-xl shadow-purple-900/30">
              <Sparkles className="w-8 h-8 text-white" />
            </div>
            <h2 className="font-head text-2xl font-bold text-slate-100">Ciao! Sono il tuo socio.</h2>
            <p className="text-slate-400 mt-2 text-sm">Conosco i conti, gli eventi e il magazzino di Betti SRL. Chiedimi qualsiasi cosa per far crescere l'azienda.</p>
            <div className="mt-8 grid gap-2.5 text-left">
              {SUGGESTIONS.map((s, i) => (
                <button key={i} data-testid={`suggestion-${i}`} onClick={() => send(s)} className="group flex items-center gap-3 bg-[#121824] border border-[#222D42] hover:border-purple-500/40 rounded-xl px-4 py-3 text-sm text-slate-300 transition-colors text-left">
                  <Sparkles className="w-4 h-4 text-purple-400 shrink-0" />
                  <span>{s}</span>
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="max-w-3xl mx-auto space-y-5 px-1">
            {messages.map((m, i) => (
              <div key={i} data-testid={`chat-message-${m.role}`} className={`flex gap-3 ${m.role === "user" ? "flex-row-reverse" : ""}`}>
                <div className={`w-8 h-8 rounded-lg shrink-0 flex items-center justify-center ${m.role === "user" ? "bg-amber-500" : "bg-gradient-to-br from-purple-600 to-indigo-600"}`}>
                  {m.role === "user" ? <User className="w-4 h-4 text-slate-950" /> : <Bot className="w-4 h-4 text-white" />}
                </div>
                <div className={`rounded-2xl px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap max-w-[85%] ${m.role === "user" ? "bg-amber-500 text-slate-950 font-medium" : "bg-[#121824] border border-[#222D42] text-slate-200"}`}>
                  {m.content || (streaming && i === messages.length - 1 ? <span className="inline-flex items-center gap-1 text-slate-500"><Loader2 className="w-3.5 h-3.5 animate-spin" /> sto pensando…</span> : "")}
                  {m.role === "assistant" && streaming && i === messages.length - 1 && m.content && <span className="cursor-blink text-purple-400">▋</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="pt-3 border-t border-[#222D42]">
        <div className="max-w-3xl mx-auto flex items-end gap-2">
          <textarea
            data-testid="chat-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder="Scrivi al tuo socio…"
            rows={1}
            className="flex-1 resize-none bg-[#121824] border border-[#222D42] rounded-xl px-4 py-3 text-sm text-slate-100 focus:outline-none focus:border-purple-500/50 max-h-32"
          />
          <button
            data-testid="chat-send-button"
            onClick={() => send()}
            disabled={streaming || !input.trim()}
            className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white font-semibold w-12 h-12 rounded-xl flex items-center justify-center shadow-lg shadow-purple-900/30 transition-colors disabled:opacity-50 shrink-0"
          >
            {streaming ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
          </button>
        </div>
      </div>
    </div>
  );
}
